import React, { useState } from 'react';
import {Helmet} from "react-helmet";

const CodeTools = () => {
  const [activeTab, setActiveTab] = useState('formatter');
  const [language, setLanguage] = useState('json');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  // Extended language options
  const formatLanguages = [
    'json', 'javascript', 'css', 'html', 'php', 'sql',
    'python', 'java', 'csharp', 'ruby', 'typescript',
    'yaml', 'xml', 'markdown', 'shell'
  ];

  const minifyLanguages = ['json', 'javascript', 'css', 'html', 'xml', 'yaml'];

  // Get current languages based on active tab
  const getLanguages = () => {
    switch (activeTab) {
      case 'formatter': return formatLanguages;
      case 'minifier': return minifyLanguages;
      case 'sql': return ['sql'];
      default: return formatLanguages;
    }
  };

  // Reset state when changing tools
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setInput('');
    setOutput('');
    setError('');

    // Set default language for the selected tab
    if (tab === 'formatter') setLanguage('json');
    else if (tab === 'minifier') setLanguage('json');
    else if (tab === 'sql') setLanguage('sql');
  };

  // Format code
  const formatCode = () => {
    setError('');
    try {
      let result = '';

      switch (language) {
        case 'json':
          const parsed = JSON.parse(input);
          result = JSON.stringify(parsed, null, 2);
          break;
        case 'javascript':
        case 'typescript':
          // Basic JS/TS formatting
          result = input.replace(/[{]\s*/g, '{\n  ')
            .replace(/[}]\s*/g, '\n}\n')
            .replace(/[;]\s*/g, ';\n  ')
            .replace(/(?<![;{}])\s*\n/g, ';\n')
            .replace(/if\s*\((.*?)\)\s*{/g, 'if ($1) {\n  ')
            .replace(/for\s*\((.*?)\)\s*{/g, 'for ($1) {\n  ')
            .replace(/while\s*\((.*?)\)\s*{/g, 'while ($1) {\n  ');
          break;
        case 'html':
        case 'xml':
          // HTML/XML formatting
          result = input.replace(/></g, '>\n<')
            .replace(/<(\w+)([^>]*)>/g, '<$1$2>\n')
            .replace(/<\/(\w+)>/g, '</$1>\n')
            .replace(/^\s*\n/gm, '');
          break;
        case 'css':
          // CSS formatting
          result = input.replace(/\s*{\s*/g, ' {\n  ')
            .replace(/;\s*/g, ';\n  ')
            .replace(/\s*}\s*/g, '\n}\n')
            .replace(/,\s*/g, ',\n');
          break;
        case 'python':
          // Basic Python formatting
          result = input.replace(/:\s*/g, ':\n  ')
            .replace(/^\s*def /gm, '\ndef ')
            .replace(/^\s*class /gm, '\nclass ')
            .replace(/^\s*if /gm, '\nif ')
            .replace(/^\s*elif /gm, '\nelif ')
            .replace(/^\s*else:/gm, '\nelse:')
            .replace(/^\s*for /gm, '\nfor ')
            .replace(/^\s*while /gm, '\nwhile ');
          break;
        case 'java':
        case 'csharp':
          // Java/C# formatting
          result = input.replace(/[{]\s*/g, ' {\n  ')
            .replace(/[}]\s*/g, '\n}\n')
            .replace(/[;]\s*/g, ';\n  ')
            .replace(/public\s+class/g, '\npublic class')
            .replace(/private\s+class/g, '\nprivate class')
            .replace(/protected\s+class/g, '\nprotected class')
            .replace(/public\s+static\s+void/g, '\n  public static void');
          break;
        case 'ruby':
          // Ruby formatting
          result = input.replace(/\s*do\s*\|([^|]*)\|\s*/g, ' do |$1|\n  ')
            .replace(/\s*end\s*/g, '\nend\n')
            .replace(/^\s*def /gm, '\ndef ')
            .replace(/^\s*class /gm, '\nclass ')
            .replace(/^\s*if /gm, '\nif ')
            .replace(/^\s*elsif /gm, '\nelsif ')
            .replace(/^\s*else/gm, '\nelse');
          break;
        case 'php':
          // PHP formatting
          result = input.replace(/<\?php/g, '<?php\n')
            .replace(/[{]\s*/g, ' {\n  ')
            .replace(/[}]\s*/g, '\n}\n')
            .replace(/[;]\s*/g, ';\n  ')
            .replace(/function\s+/g, '\nfunction ');
          break;
        case 'sql':
          // SQL formatting
          result = input.replace(/SELECT/gi, 'SELECT\n  ')
            .replace(/FROM/gi, '\nFROM\n  ')
            .replace(/WHERE/gi, '\nWHERE\n  ')
            .replace(/ORDER BY/gi, '\nORDER BY\n  ')
            .replace(/GROUP BY/gi, '\nGROUP BY\n  ')
            .replace(/HAVING/gi, '\nHAVING\n  ')
            .replace(/JOIN/gi, '\nJOIN\n  ')
            .replace(/AND/gi, '\n  AND')
            .replace(/OR/gi, '\n  OR')
            .replace(/INSERT INTO/gi, 'INSERT INTO\n  ')
            .replace(/VALUES/gi, '\nVALUES\n  ')
            .replace(/CREATE TABLE/gi, 'CREATE TABLE\n  ');
          break;
        case 'yaml':
          // Basic YAML formatting
          result = input.replace(/^([^:]+):/gm, '\n$1:')
            .replace(/^\s*-\s*/gm, '\n  - ');
          break;
        case 'markdown':
          // Markdown formatting
          result = input.replace(/^#{1,6}\s*/gm, match => '\n' + match)
            .replace(/^-\s*/gm, '\n- ')
            .replace(/^(\d+)\.\s*/gm, '\n$1. ');
          break;
        case 'shell':
          // Shell script formatting
          result = input.replace(/;\s*/g, ';\n')
            .replace(/\|\s*/g, '|\n  ')
            .replace(/>\s*/g, '>\n  ')
            .replace(/if\s*\[/g, 'if [')
            .replace(/then\s*/g, '\nthen\n  ')
            .replace(/else\s*/g, '\nelse\n  ')
            .replace(/fi\s*/g, '\nfi\n')
            .replace(/for\s*(.*)\s*in/g, 'for $1 in')
            .replace(/do\s*/g, '\ndo\n  ')
            .replace(/done\s*/g, '\ndone\n');
          break;
        default:
          result = input;
      }

      setOutput(result);
    } catch (err) {
      setError(`Error: ${err.message}`);
    }
  };

  // Minify code
  const minifyCode = () => {
    setError('');
    try {
      let result = '';

      switch (language) {
        case 'json':
          const parsed = JSON.parse(input);
          result = JSON.stringify(parsed);
          break;
        case 'javascript':
        case 'typescript':
          // Basic JS/TS minification
          result = input.replace(/\/\/.*?\n/g, '') // Remove single line comments
            .replace(/\/\*[\s\S]*?\*\//g, '') // Remove multi-line comments
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/\s*{\s*/g, '{')
            .replace(/\s*}\s*/g, '}')
            .replace(/\s*;\s*/g, ';')
            .replace(/\s*:\s*/g, ':')
            .replace(/\s*,\s*/g, ',')
            .replace(/\s*=\s*/g, '=');
          break;
        case 'html':
        case 'xml':
          // HTML/XML minification
          result = input.replace(/<!--[\s\S]*?-->/g, '') // Remove comments
            .replace(/>\s+</g, '><') // Remove whitespace between tags
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/\s+>/g, '>') // Remove whitespace before closing >
            .replace(/<\s+/g, '<'); // Remove whitespace after opening <
          break;
        case 'css':
          // CSS minification
          result = input.replace(/\/\*[\s\S]*?\*\//g, '') // Remove comments
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/\s*{\s*/g, '{')
            .replace(/\s*}\s*/g, '}')
            .replace(/\s*;\s*/g, ';')
            .replace(/\s*:\s*/g, ':')
            .replace(/;\}/g, '}') // Remove last semicolon in rule
            .replace(/\s*,\s*/g, ',');
          break;
        case 'yaml':
          // Basic YAML minification (keeping essential whitespace)
          result = input.replace(/\n\s*\n/g, '\n') // Remove empty lines
            .replace(/^\s+/gm, match => {
              // Preserve indentation levels but remove extra spaces
              return ' '.repeat(Math.floor(match.length / 2) * 2);
            });
          break;
        default:
          result = input.replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .trim();
      }

      setOutput(result);
    } catch (err) {
      setError(`Error: ${err.message}`);
    }
  };

  // Format SQL
  const formatSQL = () => {
    setError('');
    try {
      // Enhanced SQL formatting
      const result = input.replace(/SELECT/gi, 'SELECT\n  ')
        .replace(/FROM/gi, '\nFROM\n  ')
        .replace(/WHERE/gi, '\nWHERE\n  ')
        .replace(/ORDER\s+BY/gi, '\nORDER BY\n  ')
        .replace(/GROUP\s+BY/gi, '\nGROUP BY\n  ')
        .replace(/HAVING/gi, '\nHAVING\n  ')
        .replace(/JOIN/gi, '\nJOIN\n  ')
        .replace(/INNER\s+JOIN/gi, '\nINNER JOIN\n  ')
        .replace(/LEFT\s+JOIN/gi, '\nLEFT JOIN\n  ')
        .replace(/RIGHT\s+JOIN/gi, '\nRIGHT JOIN\n  ')
        .replace(/FULL\s+JOIN/gi, '\nFULL JOIN\n  ')
        .replace(/ON\s+/gi, '\n  ON ')
        .replace(/AND/gi, '\n  AND')
        .replace(/OR/gi, '\n  OR')
        .replace(/INSERT\s+INTO/gi, 'INSERT INTO\n  ')
        .replace(/VALUES\s*\(/gi, 'VALUES\n  (')
        .replace(/CREATE\s+TABLE/gi, 'CREATE TABLE\n  ')
        .replace(/DROP\s+TABLE/gi, 'DROP TABLE\n  ')
        .replace(/ALTER\s+TABLE/gi, 'ALTER TABLE\n  ')
        .replace(/UPDATE/gi, 'UPDATE\n  ')
        .replace(/SET/gi, '\nSET\n  ')
        .replace(/DELETE\s+FROM/gi, 'DELETE FROM\n  ')
        .replace(/UNION/gi, '\nUNION\n')
        .replace(/CASE/gi, '\nCASE\n  ')
        .replace(/WHEN/gi, '\n  WHEN')
        .replace(/THEN/gi, ' THEN')
        .replace(/ELSE/gi, '\n  ELSE')
        .replace(/END/gi, '\nEND');

      setOutput(result);
    } catch (err) {
      setError(`Error: ${err.message}`);
    }
  };

  // Process the code based on the active tool
  const processCode = () => {
    if (!input.trim()) {
      setError('Please enter code to process');
      return;
    }

    if (activeTab === 'formatter') formatCode();
    else if (activeTab === 'minifier') minifyCode();
    else if (activeTab === 'sql') formatSQL();
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
  };

  // Get language display name 
  const getLanguageDisplayName = (lang) => {
    const nameMap = {
      'javascript': 'JavaScript',
      'typescript': 'TypeScript',
      'csharp': 'C#',
      'json': 'JSON',
      'html': 'HTML',
      'css': 'CSS',
      'xml': 'XML',
      'yaml': 'YAML',
      'sql': 'SQL',
      'python': 'Python',
      'java': 'Java',
      'ruby': 'Ruby',
      'php': 'PHP',
      'markdown': 'Markdown',
      'shell': 'Shell/Bash'
    };

    return nameMap[lang] || lang.toUpperCase();
  };

  return (

    <>
      <Helmet>
        <title>Online Code Formatter, Minifier & SQL Beautifier - Free Tool</title>
        <meta
          name="description"
          content="Free online tool to format, minify, and beautify code. Supports JSON, JavaScript, CSS, HTML, PHP, SQL, YAML, and more. Clean and optimize your code instantly."
        />
        <meta
          name="keywords"
          content="code formatter, code beautifier, code minifier, SQL formatter, JSON formatter, CSS minifier, online code editor, code prettifier, HTML formatter"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://webutility.online/" />
        <meta property="og:title" content="Online Code Formatter & Minifier" />
        <meta
          property="og:description"
          content="Instantly format, beautify, and minify your code for free. Supports multiple languages."
        />
        <meta property="og:url" content="https://webutility.online/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://webutility.online/preview.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Online Code Formatter, Minifier & Beautifier" />
        <meta
          name="twitter:description"
          content="Free tool to clean, minify, or beautify code. Supports HTML, JS, JSON, SQL, CSS, PHP & more."
        />
        <meta name="twitter:image" content="https://webutility.online/preview.png" />
      </Helmet>
      <main className="flex-grow px-4 py-8">
        <div className="max-w-screen-2xl mx-auto">
          {/* Tool Selection */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'formatter' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleTabChange('formatter')}
            >
              Code Formatter
            </button>
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'minifier' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleTabChange('minifier')}
            >
              Code Minifier
            </button>
            <button
              className={`px-4 py-2 font-medium ${activeTab === 'sql' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleTabChange('sql')}
            >
              SQL Beautifier
            </button>
            {/* <button 
              className={`px-4 py-2 font-medium ${activeTab === 'jsonvalidator' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleTabChange('jsonvalidator')}
            >
              Json Validator
            </button>
            <button 
              className={`px-4 py-2 font-medium ${activeTab === 'regextester' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => handleTabChange('regextester')}
            >
              Regex Tester
            </button> */}
          </div>

          {/* Tool Interface */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Panel - Input */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700">Input</h2>

                {/* Language Selector */}
                {activeTab !== 'sql' && (
                  <select
                    className="border border-gray-300 rounded px-3 py-1 text-gray-700"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    {getLanguages().map((lang) => (
                      <option key={lang} value={lang}>{getLanguageDisplayName(lang)}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Code Input */}
              <textarea
                className="w-full h-64 font-mono text-sm p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`Paste your ${getLanguageDisplayName(language)} code here...`}
              />

              {/* Action Button */}
              <div className="mt-4">
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-150"
                  onClick={processCode}
                >
                  {activeTab === 'formatter' ? 'Format Code' : activeTab === 'minifier' ? 'Minify Code' : 'Beautify SQL'}
                </button>
              </div>
            </div>

            {/* Right Panel - Output */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700">Output</h2>

                {/* Copy Button */}
                {output && (
                  <button
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    onClick={copyToClipboard}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path>
                    </svg>
                    Copy
                  </button>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
                  {error}
                </div>
              )}

              {/* Code Output */}
              <div className="w-full h-64 font-mono text-sm p-3 border border-gray-300 rounded-md bg-gray-50 overflow-auto">
                <pre>{output}</pre>
              </div>
            </div>

           
          </div>
          <div className="mt-8 bg-white rounded-lg shadow-md p-6 max-w-screen-2xl">
            <h3 className="text-lg font-medium mb-4">How to Use the Code Tools</h3>
            <p className="mb-4">
              These tools help you format, minify, and beautify your code. Here's how to use them:
            </p>

            <h4 className="font-semibold">1. JavaScript Code Formatter</h4>
            <p>
              Paste your JavaScript code into the provided text area. Click the "Format Code" button to get a nicely formatted version of your code that is easy to read and maintain.
            </p>

            <h4 className="font-semibold">2. JavaScript Code Minifier</h4>
            <p>
              Paste your JavaScript code into the text area and click the "Minify Code" button. This will remove unnecessary spaces, line breaks, and other formatting to reduce the size of your code.
            </p>

            <h4 className="font-semibold">3. SQL Code Beautifier</h4>
            <p>
              Paste your SQL query into the text area and click the "Beautify SQL Code" button to structure the query properly. This will make the SQL easier to read and debug.
            </p>

            <h4 className="font-semibold">4. Copy Code</h4>
            <p>
              Once the code is formatted, minified, or beautified, you can easily copy it by selecting the formatted code and copying it to your clipboard for use in your projects.
            </p>
          </div>
        </div>
      </main>
    </>



  );
};

export default CodeTools;
